// External packages
import React, { useEffect } from 'react';

// MUI
import { styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Collapse,
  Container,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  css,
} from '@mui/material';

import { hexToCSSFilter } from 'hex-to-css-filter';

// Locale
import { t } from 'i18next';

// Local types
import {
  BigButtonProps,
  DisplayList,
  DisplayProps,
  ModuleDataType,
  SmallButtonProps,
} from '../../../types/interfacePreviewTypes';
import PulsingDot from '../hint-pulsating-dot';

interface InterfaceBackgroundContainerProps {
  foregroundimg: string | null;
  backgroundColor: string | null;
  foregroundprinting: string | null;
  modulepreviewdata?: ModuleDataType | null;
}

interface InterfaceBorderProps {
  borderclr?: string | null;
}

interface LedProps {
  iconId: number | null;
  ledIconMap?: any;
  isCustomIcon?: boolean;
  uploadedIcons: any[];
}

const LedsRowContainer = styled(Box)(() => ({
  cursor: 'pointer',
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
}));

const LedsColumnContainer = styled(Grid)(() => ({
  padding: '25% 0rem',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-evenly',
}));

const TopLedsGroupContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  gridTemplateColumns: 'repeat(3, 1fr)',
  flexWrap: 'wrap',
  gap: '2.2rem',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'end',
  paddingLeft: '0.15rem',
  paddingRight: '0.15rem',
  paddingBottom: '2.3rem',
}));

const BottomLedsGroupContainer = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  gridTemplateColumns: 'repeat(3, 1fr)',
  flexWrap: 'wrap',
  gap: '2rem',
  flex: 1,
  justifyContent: 'center',
  alignItems: 'start',
  paddingLeft: '0.15rem',
  paddingRight: '0.15rem',
  paddingTop: '2.3rem',
}));

const LedsGroupColumnContainer = styled(Grid)(() => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexWrap: 'wrap',
  padding: '0.82rem',
  justifyContent: 'center',
  alignItems: 'center',
}));

const SmallButtonsContainer = styled(Grid)(() => ({
  overflow: 'visible',
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  flexWrap: 'wrap',
  gap: '2rem',
  justifyContent: 'center',
  alignItems: 'center',
}));

const BigButtonContainer = styled(Grid)(() => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  flexWrap: 'wrap',
  gap: '1.6rem',
  justifyContent: 'center',
  alignItems: 'center',
}));

const DisplayContainer = styled(Grid)(() => ({
  width: '100%',
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  flexWrap: 'wrap',
  gap: '1.6rem',
  justifyContent: 'center',
  alignItems: 'center',
}));

const InterfaceBackgroundContainer = styled(Box)<InterfaceBackgroundContainerProps>(
  ({ foregroundprinting, foregroundimg, backgroundColor, modulepreviewdata: modulePreviewData }) => ({
    // backgroundImage: foregroundimg && modulePreviewData?.foreground.active ? `url(${foregroundimg})` : 'none',
    display: 'inline-flex',
    borderRadius: '1.1rem',
    overflow: 'visible',
    // TODO: Add aspect ratio
    // aspectRatio: `${modulePreviewData?.frame.dimensions.width.px}/${modulePreviewData?.frame.dimensions.height.px}`,
    // backgroundColor: backgroundColor || 'black',
    // backgroundSize: 'cover',

    position: 'relative',
    backgroundImage: `url(${foregroundimg})`,
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundColor: 'transparent',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      zIndex: 1,
    },
  })
);

const InterfaceBorder = styled(Box)<InterfaceBorderProps>(({ borderclr: borderColor }) => ({
  // borderRadius: '0.8rem',
  // backgroundColor: `${borderColor}`,
  minWidth: 'fit-content',
  // border: borderColor ? `14px solid ${borderColor}` : '14px solid transparent',
}));

const ScalableWrapper = styled(Box)<{ scale?: number }>(({ theme, scale }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    height: 'inherit',
  },
}));

interface InterfacePreviewProps {
  buttonRef?: (ref: HTMLDivElement | null) => void;
  ignoreUserInteraction?: boolean;
  loadingIcons: Boolean;
  handleElementClick: (e: React.MouseEvent<HTMLElement>, buttonIndex: number) => void;
  handleDisplayClick: (e: React.MouseEvent<HTMLElement>) => void;
  arrayOfIcons: any[][] | null;
  modulePreviewData: ModuleDataType;
  displayList: DisplayList[];
  id: string;
  screenshotMode?: boolean;
  handleClickHintDot: () => void;
  hintsActive: boolean;
}

function uint8ToBase64(buffer: Uint8Array | null | undefined) {
  if (!buffer || !(buffer instanceof Uint8Array)) {
    console.error('Invalid input for uint8ToBase64 at InterfacePreview/index.tsx:', buffer);
    return ''; // Return empty string or handle the error as needed.
  }

  return btoa(String.fromCharCode.apply(null, Array.from(buffer)));
}

// The first number of the Uint8Array defines the type of image
// 137 for PNG, 60 for SVG
const getMimeType = (iconData: Uint8Array | number[]) => {
  if (!iconData || iconData.length === 0) {
    console.error('Invalid icon data:', iconData);
    return '';
  }
  return iconData[0] === 137 ? 'data:image/png;base64,' : 'data:image/svg+xml;base64,';
};

// const cleanSVG = async (svgBlob: Uint8Array): Promise<Uint8Array> => {
//   const svgString = new TextDecoder('utf-8').decode(svgBlob);
//   const optimizedSVG = optimize(svgString, {
//     plugins: [
//       { removeComments: true },
//       { removeMetadata: true },
//       { removeTitle: true },
//       { name: 'removeXMLProcInst' },
//     ] as PluginConfig[],
//   });
//   return new TextEncoder().encode(optimizedSVG.data);
// };

const SmallButton: React.FC<SmallButtonProps> = ({
  uploadedIcons,
  isCustomIcon,
  buttonIndex,
  position,
  iconId,
  defaultColor,
  iconMap,
  screenshotMode,
  embossing = false,
  buttonBorder = true,
  showPulsingDot = false,
  handleClickHintDot,
  hintsActive,
}) => {
  const iconData = iconId && iconMap ? iconMap[iconId] : null;

  const svgFile = uploadedIcons.find((icon) => icon.id === iconId);

  const customIcon = svgFile?.base64 || svgFile?.url || null;

  const getIconImageForSmallButton = () => {
    if (isCustomIcon && customIcon) {
      return `${customIcon}`;
    }

    if (iconData) {
      return `${getMimeType(iconData)}${uint8ToBase64(iconData)}`;
    }
    return null;
  };

  const backgroundImage = getIconImageForSmallButton();

  return (
    <Paper
      className={`Button-col-${buttonIndex}-${position}`}
      sx={{
        cursor: 'pointer',
        width: '3.5rem',
        height: '3.5rem',

        flexGrow: 1,
        position: 'relative',
        aspectRatio: '1/1',
        backgroundColor: 'transparent',
        borderRadius: '0.15rem',
        // border: iconId ? `2px solid ${screenshotMode ? 'black' : defaultColor}` : 'none',
        border: iconId ? 'none' : `1.4px dashed white`,
        padding: '0.4rem',
        boxShadow: embossing
          ? 'inset 2px 2px 5px rgba(0,0,0,0.3), inset -1px -1px 2px rgba(255,255,255,0.3), 2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.2)'
          : 'none',
      }}
    >
      {showPulsingDot && hintsActive && (
        <Box>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 90,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <PulsingDot handleClickHintDot={handleClickHintDot} />
          </Box>
          <Card
            onClick={(e) => {
              e.stopPropagation();

              handleClickHintDot();
            }}
            className="Card"
            sx={{ position: 'absolute', top: '2.3rem', left: '1rem', zIndex: 999999 }}
          >
            <CardContent
              onClick={(e) => {
                e.stopPropagation();

                handleClickHintDot();
              }}
              sx={{ padding: '0.8rem !important', width: '100%' }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary', userSelect: 'none', width: 'max-content' }}>
                {t('hint_symbol_and_color')}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}
      <div
        // className={screenshotMode ? 'Display-col-Box-screenshot-mode' : 'Display-col-Box'}
        className="small-button"
        style={{
          height: '100%',
          width: '100%',
          // filter: 'drop-shadow(0px 5000px 0 black)',
        }}
      >
        {/* <Box className="SVG-Icon" sx={combinedStyle} /> */}
        {/* <img className="SVG-Icon" src={backgroundImage ?? ''} alt="icon" style={combinedStyle as React.CSSProperties} /> */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            backgroundColor: defaultColor,
            WebkitMaskImage: `url(${backgroundImage})`,
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            WebkitMaskSize: 'contain',
          }}
        />
      </div>
    </Paper>
  );
};

const BigButton: React.FC<BigButtonProps> = ({
  isCustomIcon,
  uploadedIcons,
  buttonIndex,
  iconId,
  defaultColor,
  iconMap,
  embossing = false,
  buttonBorder = true,
  screenshotMode,
}) => {
  const iconData = iconId && iconMap ? iconMap[iconId] : null;

  const svgFile = uploadedIcons.find((icon) => icon.id === iconId);

  const customIcon = svgFile?.base64 || svgFile?.url || null;

  const getIconImageForSmallButton = () => {
    if (isCustomIcon && customIcon) {
      return `${customIcon}`;
    }

    if (iconData) {
      return `${getMimeType(iconData)}${uint8ToBase64(iconData)}`;
    }
    return null;
  };

  const backgroundImage = getIconImageForSmallButton();
  const cssFilter = hexToCSSFilter(defaultColor);

  const baseStyle = {
    width: '100%',
    height: '100%',
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    backgroundColor: 'transparent',
    backgroundRepeat: 'no-repeat',
    filter: `drop-shadow(0px 5000px 0 ${defaultColor})`,
    transform: 'translateY(-5000px)',
  };

  const combinedStyle = {
    ...baseStyle,
  };

  return (
    <Paper
      className={`Big-Button-col-${buttonIndex}`}
      sx={{
        cursor: 'pointer',
        flexGrow: 1,
        aspectRatio: '1/1',
        backgroundColor: iconId ? 'transparent' : defaultColor,
        width: '6.5rem',
        height: '6.5rem',
        borderRadius: '0.60rem',
        // border: iconId ? `2px solid ${screenshotMode ? 'black' : defaultColor}` : 'none',
        border: iconId ? `2px solid ${buttonBorder && !screenshotMode ? defaultColor : 'transparent'}` : 'none',
        padding: '1rem',
        boxShadow: embossing
          ? 'inset 2px 2px 5px rgba(0,0,0,0.3), inset -1px -1px 2px rgba(255,255,255,0.3), 2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.2)'
          : 'none',
      }}
    >
      <Box style={combinedStyle} />
    </Paper>
  );
};

// const Display: React.FC<DisplayProps> = ({ modulePreviewData, displayList }) => {
//   const displayId = modulePreviewData.elements.display.selectionId;
//   let displayImage =
//     displayList.find((display) => display.id === displayId)?.image.horizontal || displayList[0].image.horizontal;

//   if (modulePreviewData.rotation === '90' || modulePreviewData.rotation === '270') {
//     displayImage =
//       displayList.find((display) => display.id === displayId)?.image.vertical || displayList[0].image.vertical;
//   }

//   const displaySelected = displayList.find((display) => display.id === displayId);
//   const displayType = displaySelected?.type;

//   return (
//     <Paper
//       className="Display-col"
//       sx={{
//         cursor: 'pointer',
//         flexGrow: 1,
//         aspectRatio: displayType === 'tft' ? '4/3' : '2/1',
//         height: displayType === 'tft' ? '8.6rem' : '8.1rem',
//         marginX: displayType === 'tft' ? '2rem' : '0rem',
//         borderRadius: '0.6rem',
//         overflow: 'hidden',
//       }}
//     >
//       <div
//         style={{
//           height: '100%',
//           width: '100%',
//         }}
//       >
//         <Box
//           style={{
//             width: '100%',
//             height: '100%',
//             backgroundImage: `url(${displayImage})`,
//             backgroundPosition: 'center',
//             backgroundSize: 'cover',
//             backgroundColor: 'black',
//             backgroundRepeat: 'no-repeat',
//           }}
//         />
//       </div>
//     </Paper>
//   );
// };

const InterfacePreview = React.forwardRef<HTMLDivElement, InterfacePreviewProps>(
  (
    {
      buttonRef,
      modulePreviewData,
      handleElementClick,
      handleDisplayClick,
      arrayOfIcons,
      loadingIcons,
      displayList,
      ignoreUserInteraction,
      id,
      screenshotMode = false,
      handleClickHintDot,
      hintsActive,
    },
    ref
  ) => {
    const iconMap =
      arrayOfIcons?.reduce((acc, iconData) => {
        const iconId = iconData[1];
        const iconUint8Array = iconData[10];
        acc[iconId] = iconUint8Array;
        return acc;
      }, {} as Record<number, Uint8Array>) || {};

    const Led = styled(Box, {
      shouldForwardProp: (prop) =>
        prop !== 'iconId' && prop !== 'ledIconMap' && prop !== 'isCustomIcon' && prop !== 'uploadedIcons',
    })<LedProps>(({ iconId, ledIconMap, isCustomIcon, uploadedIcons }) => {
      const iconData = iconId !== null && ledIconMap ? ledIconMap[iconId] : null;
      const svgFile = uploadedIcons.find((icon) => icon.id === iconId);
      const customIcon = svgFile?.base64 || svgFile?.url || null;

      const getIconImageForLed = () => {
        if (isCustomIcon && customIcon) {
          return `${customIcon}`;
        }
        if (iconData) {
          return `${getMimeType(iconData)}${uint8ToBase64(iconData)}`;
        }
        return null;
      };

      const backgroundImage = getIconImageForLed();

      return {
        height: '0.7rem',
        width: '0.7rem',
        cursor: 'pointer',
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: 'transparent',
        transform: 'translateY(-5000px)',
      };
    });

    const lengthSmallButtons = Object.keys(modulePreviewData.elements.symbols).length;

    const interfaceForegroundImage = modulePreviewData.background.imageUrl;

    if (loadingIcons || !arrayOfIcons || !modulePreviewData) {
      return <div>Loading...</div>;
    }

    return (
      <Container id={id} sx={{ padding: { xs: '0rem' } }} className="Panel-wrapper-Container full-height">
        <Grid className="Panel-wrapper-Grid full-height" sx={{ justifyContent: 'center' }} container>
          <Box
            className="Orientational-Container"
            sx={{
              transition: '0.5s',
              // transform: modulePreviewData.orientation === 'horizontal' ? 'rotate(-90deg)' : null,
              transform:
                modulePreviewData.rotation === '90'
                  ? 'rotate(90deg)'
                  : modulePreviewData.rotation === '180'
                  ? 'rotate(180deg)'
                  : modulePreviewData.rotation === '270'
                  ? 'rotate(270deg)'
                  : modulePreviewData.rotation === '0'
                  ? 'rotate(0deg)'
                  : null,
            }}
          >
            <Grid
              sx={{
                overflowX: {
                  xs: !screenshotMode ? 'visible' : 'inherit',
                  lg: 'inherit',
                },
              }}
              className="Model-preview-wrapper-Grid"
              item
            >
              <ScalableWrapper ref={ref}>
                <InterfaceBorder className="Interface-Border" borderclr={null}>
                  <InterfaceBackgroundContainer
                    foregroundimg={interfaceForegroundImage}
                    foregroundprinting={null}
                    backgroundColor={null}
                    modulepreviewdata={modulePreviewData}
                    className="Interface-Background-Container"
                  >
                    {/* Left LEDs */}
                    <Box sx={{ zIndex: 2, overflow: 'visible' }}>
                      <LedsGroupColumnContainer>
                        <LedsColumnContainer>
                          {/* {Object.values(modulePreviewData.elements.statusBarLeft.leds).map((led, index) => (
                          <Box
                            key={index}
                            className="Orientational-Container"
                            sx={{
                              transition: '0.5s',
                              transform: modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                            }}
                          >
                            <Led
                              key={index}
                              ledIconMap={iconMap}
                              iconId={led.icon || null}
                              isCustomIcon={led.useCustomIcon || false}
                              uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                              style={{
                                visibility: modulePreviewData.elements.statusBarLeft.leds[index].active
                                  ? 'visible'
                                  : 'hidden',
                                filter: `drop-shadow(0px 5000px 0 ${led?.color || '#000000'})`,
                              }}
                            />
                          </Box>
                        ))} */}
                        </LedsColumnContainer>
                      </LedsGroupColumnContainer>
                    </Box>

                    {/* Middle LEDs and Buttons */}
                    <Box
                      className="Middle-Container"
                      sx={{
                        overflow: 'visible',
                        width: '100%',
                        display: 'flex',
                        gap: '0.4rem',
                        justifyContent: 'space-between',
                        zIndex: '2',
                      }}
                    >
                      {Array(lengthSmallButtons / 2)
                        .fill(0)
                        .map((_, columnIndex) => (
                          <Box
                            key={columnIndex}
                            className={`Column-${columnIndex + 1}`}
                            sx={{
                              overflow: 'visible',
                              display: 'flex',
                              flexDirection: 'column',
                              flex: 1,
                            }}
                          >
                            <TopLedsGroupContainer className="Top-Leds-Group-Container">
                              <LedsRowContainer
                                onClick={(e) => !ignoreUserInteraction && handleElementClick(e, columnIndex * 2 + 1)}
                                className="Leds-Row-Container"
                              >
                                {/* {modulePreviewData.elements.smallButtons[columnIndex * 2 + 1]?.leds
                                ? Object.values(modulePreviewData.elements.smallButtons[columnIndex * 2 + 1].leds!).map(
                                    (led, ledIndex) => (
                                      <Box
                                        key={ledIndex}
                                        className="Orientational-Container"
                                        sx={{
                                          transition: '0.5s',
                                          transform:
                                            modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                                        }}
                                      >
                                        <Led
                                          className="Led"
                                          uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                          key={ledIndex}
                                          ledIconMap={iconMap}
                                          iconId={led.icon || null}
                                          style={{
                                            visibility: modulePreviewData.elements.smallButtons[columnIndex * 2 + 1]
                                              .leds![ledIndex].active
                                              ? 'visible'
                                              : 'hidden',
                                            filter: `drop-shadow(0px 5000px 0 ${led?.color || '#000000'})`,
                                          }}
                                        />
                                      </Box>
                                    )
                                  )
                                : null} */}
                              </LedsRowContainer>
                            </TopLedsGroupContainer>
                            <SmallButtonsContainer className="Small-Buttons-Container">
                              {/* Top Button */}
                              <Box
                                className="Top-Button-Container"
                                sx={{
                                  overflow: 'visible',
                                  display: 'flex',
                                  visibility: modulePreviewData.elements.symbols[columnIndex * 2 + 1].active
                                    ? 'visible'
                                    : 'hidden',
                                  pointerEvents: modulePreviewData.elements.symbols[columnIndex * 2 + 1].active
                                    ? 'auto'
                                    : 'none',
                                  justifyContent: 'center',
                                }}
                                ref={columnIndex === 0 ? buttonRef : null}
                                onClick={(e) => !ignoreUserInteraction && handleElementClick(e, columnIndex * 2 + 1)}
                              >
                                <Box
                                  className="Orientational-Container"
                                  sx={{
                                    overflow: 'visible',
                                    transition: '0.5s',
                                    transform:
                                      modulePreviewData.rotation === '0'
                                        ? 'rotate(0deg)'
                                        : modulePreviewData.rotation === '90'
                                        ? 'rotate(-90deg)'
                                        : modulePreviewData.rotation === '180'
                                        ? 'rotate(-180deg)'
                                        : modulePreviewData.rotation === '270'
                                        ? 'rotate(-270deg)'
                                        : null,
                                  }}
                                >
                                  <SmallButton
                                    hintsActive={hintsActive}
                                    handleClickHintDot={handleClickHintDot}
                                    showPulsingDot={columnIndex === 0}
                                    embossing={false}
                                    buttonBorder={false}
                                    screenshotMode={screenshotMode}
                                    buttonIndex={columnIndex * 2 + 1}
                                    position="top"
                                    uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                    isCustomIcon={
                                      modulePreviewData.elements.symbols[columnIndex * 2 + 1].useCustomSymbol || false
                                    }
                                    iconId={modulePreviewData.elements.symbols[columnIndex * 2 + 1].icon || null}
                                    defaultColor={
                                      modulePreviewData.elements.symbols[columnIndex * 2 + 1]?.color || '#000000'
                                    }
                                    iconMap={iconMap}
                                  />
                                </Box>
                              </Box>

                              {/* Bottom Button */}
                              <Box
                                className="Bottom-Button-Container"
                                sx={{
                                  display: 'flex',
                                  visibility: modulePreviewData.elements.symbols[columnIndex * 2].active
                                    ? 'visible'
                                    : 'hidden',
                                  pointerEvents: modulePreviewData.elements.symbols[columnIndex * 2].active
                                    ? 'auto'
                                    : 'none',
                                  justifyContent: 'center',
                                  position: 'relative',
                                  translate: '0px -1px',
                                }}
                                onClick={(e) => !ignoreUserInteraction && handleElementClick(e, columnIndex * 2)}
                              >
                                <Box
                                  className="Orientational-Container"
                                  sx={{
                                    transition: '0.5s',
                                    transform:
                                      modulePreviewData.rotation === '0'
                                        ? 'rotate(0deg)'
                                        : modulePreviewData.rotation === '90'
                                        ? 'rotate(-90deg)'
                                        : modulePreviewData.rotation === '180'
                                        ? 'rotate(-180deg)'
                                        : modulePreviewData.rotation === '270'
                                        ? 'rotate(-270deg)'
                                        : null,
                                  }}
                                >
                                  <SmallButton
                                    hintsActive={hintsActive}
                                    handleClickHintDot={handleClickHintDot}
                                    embossing={false}
                                    buttonBorder={false}
                                    screenshotMode={screenshotMode}
                                    buttonIndex={columnIndex * 2}
                                    uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                    position="bottom"
                                    isCustomIcon={
                                      modulePreviewData.elements.symbols[columnIndex * 2].useCustomSymbol || false
                                    }
                                    iconId={modulePreviewData.elements.symbols[columnIndex * 2].icon}
                                    defaultColor={
                                      modulePreviewData.elements.symbols[columnIndex * 2]?.color || '#000000'
                                    }
                                    iconMap={iconMap}
                                  />
                                </Box>
                              </Box>
                            </SmallButtonsContainer>

                            <BottomLedsGroupContainer className="Bottom-Leds-Group-Container">
                              <LedsRowContainer
                                onClick={(e) => !ignoreUserInteraction && handleElementClick(e, columnIndex * 2)}
                              >
                                {/* {modulePreviewData.elements.smallButtons[columnIndex * 2].leds
                                ? Object.values(modulePreviewData.elements.smallButtons[columnIndex * 2].leds!).map(
                                    (led, ledIndex) => (
                                      <Box
                                        className="Orientational-Container"
                                        key={ledIndex}
                                        sx={{
                                          transition: '0.5s',
                                          transform:
                                            modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                                        }}
                                      >
                                        <Led
                                          className="Led"
                                          uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                          key={ledIndex}
                                          ledIconMap={iconMap}
                                          iconId={led.icon || null}
                                          style={{
                                            visibility: modulePreviewData.elements.smallButtons[columnIndex * 2]?.leds
                                              ? modulePreviewData.elements.smallButtons[columnIndex * 2].leds![ledIndex]
                                                  .active
                                                ? 'visible'
                                                : 'hidden'
                                              : 'hidden',
                                            filter: `drop-shadow(0px 5000px 0 ${led?.color || '#000000'})`,
                                          }}
                                        />
                                      </Box>
                                    )
                                  )
                                : null} */}
                              </LedsRowContainer>
                            </BottomLedsGroupContainer>
                          </Box>
                        ))}

                      {/* Display */}

                      {/* {modulePreviewData.elements.display.active === true && (
                      <Box
                        className="Display-Column"
                        sx={{
                          display: 'flex',
                          px: '1.7rem',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: 1,
                        }}
                      >
                        <Tooltip placement="top" title={t('select_display')} enterDelay={500} leaveDelay={200}>
                          <DisplayContainer className="Display-Container">
                            <Box
                              className="Display-Sub-Container"
                              sx={{ display: 'flex', justifyContent: 'center' }}
                              onClick={(e) => !ignoreUserInteraction && handleDisplayClick(e)}
                            >
                              <Display modulePreviewData={modulePreviewData} displayList={displayList} />
                            </Box>
                          </DisplayContainer>
                        </Tooltip>
                      </Box>
                    )} */}

                      {/* Big Button */}
                      {/* {modulePreviewData.elements.bigButton.active === true && (
                      <Box
                        className="Big-Button-Column"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          flex: 1,
                          marginLeft: modulePreviewData.elements.display.active ? 0 : '0.8rem',
                        }}
                      >
                        <TopLedsGroupContainer className="Top-Leds-Group-Container">
                          <LedsRowContainer
                            onClick={(e) => !ignoreUserInteraction && handleElementClick(e, -1)}
                            className="Leds-Row-Container"
                          >
                            {Object.values(modulePreviewData.elements.bigButton.leds)
                              .slice(0, 6)
                              .map((led, ledIndex) => (
                                <Box
                                  key={ledIndex}
                                  className="Orientational-Container"
                                  sx={{
                                    transition: '0.5s',
                                    transform: modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                                  }}
                                >
                                  <Led
                                    className="Led"
                                    uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                    key={ledIndex}
                                    ledIconMap={iconMap}
                                    iconId={led.icon || null}
                                    style={{
                                      visibility: modulePreviewData.elements.bigButton.leds[ledIndex].active
                                        ? 'visible'
                                        : 'hidden',
                                      filter: `drop-shadow(0px 5000px 0 ${led?.color || '#000000'})`,
                                    }}
                                  />
                                </Box>
                              ))}
                          </LedsRowContainer>
                        </TopLedsGroupContainer>
                        <BigButtonContainer className="Big-Button-Container">
                          <Box
                            className="Big-Button-Sub-Container"
                            sx={{ display: 'flex', justifyContent: 'center' }}
                            onClick={(e) => !ignoreUserInteraction && handleElementClick(e, -1)}
                          >
                            <Box
                              className="Orientational-Container"
                              sx={{
                                transition: '0.5s',
                                transform: modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                              }}
                            >
                              <BigButton
                                screenshotMode={screenshotMode}
                                buttonIndex={-1}
                                embossing={modulePreviewData.elements.bigButton.embossing}
                                buttonBorder={modulePreviewData.elements.bigButton.framedBorder}
                                uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                isCustomIcon={modulePreviewData.elements.bigButton.useCustomIcon || false}
                                iconId={modulePreviewData.elements.bigButton.icon || null}
                                defaultColor={modulePreviewData.elements.bigButton?.color || '#000000'}
                                iconMap={iconMap}
                              />
                            </Box>
                          </Box>
                        </BigButtonContainer>

                        <BottomLedsGroupContainer className="Bottom-Leds-Group-Container">
                          <LedsRowContainer
                            onClick={(e) => !ignoreUserInteraction && handleElementClick(e, -1)}
                            className="Leds-Row-Container"
                          >
                            {Object.values(modulePreviewData.elements.bigButton.leds)
                              .slice(6, 13)
                              .map((led, ledIndex) => (
                                <Box
                                  key={ledIndex}
                                  className="Orientational-Container"
                                  sx={{
                                    transition: '0.5s',
                                    transform: modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                                  }}
                                >
                                  <Led
                                    className="Led"
                                    uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                                    key={ledIndex}
                                    ledIconMap={iconMap}
                                    iconId={led.icon || null}
                                    style={{
                                      visibility: modulePreviewData.elements.bigButton.leds[ledIndex + 6].active
                                        ? 'visible'
                                        : 'hidden',
                                      filter: `drop-shadow(0px 5000px 0 ${led?.color || '#000000'})`,
                                    }}
                                  />
                                </Box>
                              ))}
                          </LedsRowContainer>
                        </BottomLedsGroupContainer>
                      </Box>
                    )} */}
                    </Box>

                    {/* Right LEDs */}
                    <Box sx={{ zIndex: '2' }}>
                      <LedsGroupColumnContainer>
                        <LedsColumnContainer>
                          {/* {Object.values(modulePreviewData.elements.statusBarRight.leds).map((led, index) => (
                          <Box
                            className="Orientational-Container"
                            key={index}
                            sx={{
                              transition: '0.5s',
                              transform: modulePreviewData.orientation === 'vertical' ? 'rotate(-90deg)' : null,
                            }}
                          >
                            <Led
                              key={index}
                              uploadedIcons={modulePreviewData?.userUploadedFiles || []}
                              ledIconMap={iconMap}
                              iconId={led.icon || null}
                              isCustomIcon={led.useCustomIcon || false}
                              style={{
                                visibility: modulePreviewData.elements.statusBarRight.leds[index].active
                                  ? 'visible'
                                  : 'hidden',
                                filter: `drop-shadow(0px 5000px 0 ${led?.color || '#000000'})`,
                              }}
                            />
                          </Box>
                        ))} */}
                        </LedsColumnContainer>
                      </LedsGroupColumnContainer>
                    </Box>
                  </InterfaceBackgroundContainer>
                </InterfaceBorder>
              </ScalableWrapper>
            </Grid>
          </Box>
        </Grid>
      </Container>
    );
  }
);

export default InterfacePreview;
